/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import googleMaps from '../util/google-maps.js';
import util from '../util/util.js';
import areasMap from '../map/significant-areas.js';
import regionsMap from '../map/regions.js';
import markerLayer from '../map/remote-marker-layer.js';
import mapStyles from '../map/styles.js';
import createPagedSightingList from './shared/paged-sighting-list.js';
import DensityMapType from '../map/density-map-type.js';

// import moment from "dayjs";

import DataFiltersViewModel from "../ui/shared/data-filters.js";

let VettingViewModel = function() {
	let self = this;

	let loadPromises = [];

	function load(prom) {
		loadPromises.push(prom);
		return prom;
	}

	window.m = self; // TODO: delete me (debugging)

	self.iconSprite = require('open-iconic/sprite/open-iconic.min.svg');

	self.loaded = ko.observable(false);
	self.mapOptions = ko.observable();
	self.mapBounds = ko.observable();
	self.map = ko.observable();
	self.unthrottledFilterCriteria = ko.observable();
	self.filterCriteria = ko.observable();
	self.currentCursorData = ko.observable({});
	self.selectedSurveyLoading = ko.observable(false);
	self.selectedSurvey = ko.observable();
	self.vettingHistory = ko.observable();
	self.species = ko.observable([]);
	self.resizeState = ko.observable(false);
	self.vettingStatus = ko.observable();
	self.sightingsError = ko.observable(false);

	self.permissionDenied = ko.observable(false);

	birdata.speciesList().then(self.species);

	ko.computed(function() {
		if(self.map() && self.filterCriteria()) {
			setupMap(self.map());
		}
	});

	var selectedSightings = new Set();

	self.sortOptions = ko.observable();
	self.sort = ko.observable();
	load(birdata.vettableSightingsSortOptions()
		.then(options => {
			for(let option of options) {
				option.label = "Sort by: " + option.name
			}
			self.sortOptions(options),
			self.sort(options[2])
		}));

	load(birdata.lookup()
		.then(lookups => {
			self.vettingStatusOptions = lookups.vetting_status;
			let code = initialState.vetting_status || 'queried';
			self.vettingStatus(lookups.vetting_status.find(x => x.code === code));
		}));

	self.vettingStats = ko.observable({ state: 'init' });

	self.filterCriteriaJSON = ko.pureComputed(function() {
		return JSON.stringify(self.filterCriteria(), undefined, 2);
	});

	self.sightings = ko.observable([]);
	self.sightingsLoaded = ko.observable(false);

	self.showSightings = ko.pureComputed(function() {
		return self.sightings().length > 0 && self.vettingStats().state == 'loaded';
	});

	self.showNoSightings = ko.pureComputed(function() {
		return self.filterCriteria() && !self.sightingsError() && self.sightings().length == 0 && self.vettingStats().state == 'loaded' && (self.vettingStats().totalCount == 0 || self.filterCriteria().vetting_status != 'queried');
	});

	self.showAllVetted = ko.pureComputed(function() {
		return self.filterCriteria() && !self.sightingsError() && self.filterCriteria().vetting_status == 'queried' && self.sightings().length == 0 && self.vettingStats().state == 'loaded' && self.vettingStats().totalCount > 0;
	});

	self.selectAllChecked = ko.observable(false);
	self.selectAllIndeterminate = ko.observable(false);
	self.allSelected = ko.observable(false);

	self.anySelected = ko.pureComputed(function() {
		return self.selectionCount() > 0;
	});

	self.selectionCount = ko.pureComputed(function() {
		if(self.allSelected()) {
			return self.totalSightingCount();
		} else {
			// Create subscription on selected status of all items on page
			self.sightings().forEach(s => s.selected());
			return selectedSightings.size;
		}
	});

	self.selectedMessage = ko.pureComputed(function() {
		return util.formatQuantity(self.selectionCount(), "sighting") + " selected";
	})

	self.filters = {
		multipleSpeciesFilter: ko.observableArray(),
		regionFilter: ko.observable(null),
		programFilter: ko.observable(null),
		areaLayerFilter: ko.observable(null),
		areaFilter: ko.observable(null),
		visibleOnMapFilter: ko.observable(false),
		sourceFilter: ko.observable(),
		afterDateFilter: ko.observable(),
		beforeDateFilter: ko.observable(),
		monthsFilter: ko.observable(),
		criteria: self.unthrottledFilterCriteria,
		mapBounds: self.mapBounds,
		polygonFilter: ko.observable(null),
		map: self.map,
		userFilter: ko.observable(null),
		minCountFilter: ko.observable(),
		maxCountFilter: ko.observable()
	};

	ko.computed(function() {
		if(self.resizeState() === 'idle') {
			let originalCriteria = self.unthrottledFilterCriteria();
			if(originalCriteria) {
				let criteria = { ...originalCriteria };
				if(self.vettingStatus()) {
					criteria.vetting_status = self.vettingStatus().code;
				}

				if(util.encodeParams(criteria) !== util.encodeParams(self.filterCriteria())) {
					self.filterCriteria(criteria);
				}
			}
		}
	});

	self.canEditSpecies = ko.pureComputed(function() {
		return isABC();
	});

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		if(initialState.map) {
			var x = initialState.map.split("_");
			center = new google.maps.LatLng(+x[0], +x[1]);
			zoom = +x[2];
		}

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom
		}));
	});

	let currentLoadingToken = {};
	ko.computed(() => {
		let criteria = self.filterCriteria();
		self.sort(); // Subscribe to changes
		ko.ignoreDependencies(() => {
			if(criteria) {
				self.permissionDenied(false);
				self.vettingStats({ state: 'loading' });
				self.clearSelection();
				refreshVettingStats();
				updateSightings(criteria);
			}
		});
	});

	function refreshVettingStats() {
		let criteria = { ...self.filterCriteria() };
		delete criteria.vetting_status;
		let loadingToken = currentLoadingToken = {};
		birdata.vettingStats(criteria).then(stats => {
			if(loadingToken === currentLoadingToken) {
				updateVettingStats(stats);
			}
		}).catch(function(error) {
			if(error.xhr && error.xhr.status === 403) {
				self.permissionDenied(true);
				mePromise.then(me =>
					autoSelectFiltersForPermissions(me));
			} else {
				throw error;
			}
		});
	}

	self.nextPage = function nextPage() {
		let criteria = self.filterCriteria();
		let sightings = self.sightings();
		let cursorData;
		if(sightings.length == 0) {
			cursorData = {};
		} else {
			let last = sightings[sightings.length - 1];
			if(last.last) {
				return;
			}
			cursorData = {
				cursor: last.cursor,
				approximate_offset: last.approximate_offset
			};
		}
		updateSightings(criteria, cursorData)
	}

	self.nextEnabled = ko.pureComputed(function() {
		let sightings = self.sightings();
		return sightings.length > 0 && !sightings[sightings.length - 1].last;
	});

	self.previousPage = function previousPage() {
		let criteria = self.filterCriteria();
		let sightings = self.sightings();
		let cursorData;
		if(sightings.length == 0) {
			cursorData = {};
		} else {
			let first = sightings[0]
			if(first.first) {
				return;
			}
			cursorData = {
				cursor: first.cursor,
				approximate_offset: first.approximate_offset,
				previous: true
			};
		}
		updateSightings(criteria, cursorData)
	}

	self.previousEnabled = ko.pureComputed(function() {
		let sightings = self.sightings();
		return sightings.length > 0 && !sightings[0].first;
	});

	self.currentPageRange = ko.pureComputed(function() {
		let sightings = self.sightings();
		if(sightings.length > 0) {
			return util.formatNumber(sightings[0].approximate_offset) + "–" + util.formatNumber(sightings[sightings.length - 1].approximate_offset) + " / " + util.formatNumber(self.totalSightingCount());
		} else {
			return "";
		}
	});

	self.pageSelectionStatus = ko.pureComputed(function() {
		let sightings = self.sightings();
		let selectedCount = sightings.filter(s => s.selected()).length;

		if(selectedCount == 0) {
			return "none";
		} else if(selectedCount == sightings.length) {
			return "all";
		} else {
			return "some";
		}
	});

	self.handleSelectAllChange = function(model, evt) {
		let status = self.pageSelectionStatus();
		if(status == "none") {
			self.sightings().forEach(s => s.selected(true));
		} else {
			self.sightings().forEach(s => s.selected(false));
			// self.clearSelection();
		}
	}

	self.pageSelectionStatus.subscribe(function(status) {
		self.selectAllIndeterminate(status == "some");
		self.selectAllChecked(status == "all");
	});

	self.pageSelectedMessage = ko.pureComputed(function() {
		let pageSize = self.sightings().length
		if(!self.allSelected() && self.pageSelectionStatus() == "all" && pageSize > 1 && pageSize < self.totalSightingCount()) {
			return "All " + util.formatQuantity(pageSize, "sighting") + " on this page are selected";
		}
	});

	self.totalSightingCount = ko.observable(0);

	self.selectAllMessage = ko.pureComputed(function() {
		return "Select all " + util.formatQuantity(self.totalSightingCount(), "sighting");
	});

	self.selectAll = function() {
		self.allSelected(true);
		self.sightings().forEach(s => s.selected(true));
	}

	self.allSelectedMessage = ko.pureComputed(function() {
		if(self.allSelected()) {
			return "All " + util.formatQuantity(self.totalSightingCount(), "sighting") + " selected";
		}
	});

	self.clearSelection = function() {
		self.sightings().forEach(s => s.selected(false));
		selectedSightings.clear();
		self.allSelected(false);
	};

	let sightingLoadCounter = 0;
	function updateSightings(criteria, cursorData) {
		let token = ++sightingLoadCounter;
		self.sightingsError(false);

		// self.sightings([]);
		self.sightingsLoaded(false);
		let params = {
			sort: self.sort().code,
			...criteria,
			...cursorData
		}
		birdata.vettableSightings(params)
		.then(sightings => {
			if(token != sightingLoadCounter) {
				return; // Ignore stale response
			}
			self.sightings(sightings.map(sightingModel));
			self.currentCursorData(cursorData);
			self.sightingsLoaded(true);
		})
		.catch(error => {
			if(token != sightingLoadCounter) {
				return; // Ignore stale response
			}
			let isTimeout = false;
			try {
				isTimeout = error.xhr.responseText.indexOf("Unable to respond in time") != -1;
			} catch {}
			self.sightings([]);
			self.sightingsLoaded(true);
			if(isTimeout) {
				self.sightingsError("Please try narrowing the filtering criteria down to show a smaller number of sightings");
			} else {
				self.sightingsError(" ");
				throw error;
			}
		});
	}

	function togglePanel(panel, activePanel, reviewNotes, evt) {
		reviewNotes('');
		let selector = ".panel." + panel;
		if(panel == activePanel()) {
			activePanel(null);
		} else {
			activePanel(panel);
			setTimeout(function() {
				let panel = evt.target.parentElement.querySelector(selector);
				if(panel) {
					panel.scrollIntoView({block: "nearest", inline: "nearest", behaviour: "smooth"});
					panel.querySelector("textarea").focus();
				}
			})
		}
	}

	function showSightingMarker(s) {
		if(self.sightingMarker) {
			self.sightingMarker.setPosition({ lat: s.lat, lng: s.lon });
			self.sightingMarker.setVisible(true);
		}
	}

	function hideSightingMarker() {
		if(self.sightingMarker) {
			self.sightingMarker.setVisible(false);
		}
	}

	function sightingModel(s) {
		let countAndBreeding = s.individual_count ? util.formatNumber(s.individual_count) : "P";
		if(s.breeding_activity && s.breeding_activity !== "None") {
			countAndBreeding += ", " + s.breeding_activity;
		}

		let locationDescription = s.survey_point_name;
		if(s.accuracy_in_metres) {
			locationDescription += " (<" + util.formatDistance(s.accuracy_in_metres) + ")";
		}

		let date = util.formatDate(s.start_date);

		let notes = "";
		if(s.survey_notes) {
			notes += "Survey notes: " + s.survey_notes + " ";
		}
		if(s.sighting_notes) {
			notes += "Sighting notes: " + s.sighting_notes;
		}

		let selected = ko.observable(selectedSightings.has(s.id) || self.allSelected());
		selected.subscribe(isSelected => {
			if(isSelected) {
				selectedSightings.add(model.id);
			} else {
				selectedSightings.delete(model.id);
				self.allSelected(false);
			}
		});

		let updating = ko.observable(false);

		let activePanel = ko.observable();
		let showAcceptPanel = ko.pureComputed(function() {
			return activePanel() == 'accept';
		});
		let showRejectPanel = ko.pureComputed(function() {
			return activePanel() == 'reject';
		});
		let showOtherPanel = ko.pureComputed(function() {
			return activePanel() == 'other';
		})

		let reviewNotes = ko.observable('');
		let otherStatus = ko.observable();

		function toggleAcceptPanel(model, evt) {
			togglePanel("accept", activePanel, reviewNotes, evt);
		}

		function toggleRejectPanel(model, evt) {
			togglePanel("reject", activePanel, reviewNotes, evt);
		}

		function toggleOtherPanel(model, evt) {
			otherStatus(self.vettingStatusOptions.find(x => x.code === s.vetting_status_code));
			togglePanel("other", activePanel, reviewNotes, evt);
		}

		function confirm(status, speciesId) {
			updating(true);

			let reviewParams = {
				notes:reviewNotes().trim(),
				vetting_status: status
			};

			if(speciesId) {
				reviewParams.species_id = speciesId;
			}

			return birdata.submitVettingReview(s.id, reviewParams).then(function(sighting) {
				refreshVettingStats();
				selectedSightings.delete(model.id);
				return updateSightings(self.filterCriteria(), self.currentCursorData());
			}).catch(function(error) {
				alert("Error while updating vetting status");
				console.log(error);
			}).finally(function() {
				updating(false);
			});
		}

		function confirmAccept() {
			confirm('accepted');
		}

		function confirmReject() {
			confirm('rejected');
		}

		function confirmOther() {
			confirm(otherStatus().code);
		}

		function viewSurvey() {
			self.selectedSurveyLoading(true);
			birdata.survey(s.survey_id)
				.then(self.selectedSurvey)
				.finally(() => self.selectedSurveyLoading(false));
		}

		function viewHistory() {
			self.vettingHistory({ loading: true, id: s.id, reviews: [] });
			birdata.vettingReviews(s.id).then(function(reviews) {
				self.vettingHistory({ loading: false, reviews: reviews.map(reviewModel) })
			});
		}

		function zoomTo() {
			let map = self.map();
			if(map) {
				map.setCenter({ lat: s.lat, lng: s.lon });
				map.setZoom(14);
			}
		}

		function reviewModel(r) {
			return {
				timestamp: new Date(Date.parse(r.time_created)).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short'}),
				vetterName: r.vetter_name || 'System',
				vettingStatus: r.vetting_status_name,
				vettingStatusClass: "vetting-status vetting-status-" + r.vetting_status_code,
				color: statusColours[r.vetting_status_code],
				notes: r.notes,
				json: JSON.stringify(r, null, 2)
			}
		}

		function editSpecies() {
			showEditSpeciesDialog((species) => confirm('accepted', species.id), reviewNotes);
		}

		let active = ko.pureComputed(function() {
			return !!activePanel();
		});

		function handleMouseenter() {
			showSightingMarker(s);
		}

		function handleMouseleave() {
			hideSightingMarker();
		}

		let model = {
			...s,
			sightingDescription: s.common_name + " (" + countAndBreeding + ")",
			vettingStatus: s.vetting_status,
			vettingStatusClass: "vetting-status vetting-status-" + s.vetting_status_code,
			otherStatus,
			locationDescription,
			date,
			surveyType: s.survey_type,
			program: s.program,
			experience: s.experience,
			observer: s.observer,
			notes,
			vettingNotes: s.vetting_notes,
			selected,
			json: JSON.stringify(s, undefined, 2),
			showAcceptPanel,
			toggleAcceptPanel,
			showRejectPanel,
			toggleRejectPanel,
			toggleOtherPanel,
			showOtherPanel,
			active,
			reviewNotes,
			confirmAccept,
			confirmReject,
			confirmOther,
			viewSurvey,
			viewHistory,
			canEditSpecies: self.canEditSpecies,
			editSpecies,
			zoomTo,
			handleMouseenter,
			handleMouseleave
		}

		return model
	}

	let statusColours = {
		accepted: 'purple',
		within_range: 'green',
		queried: 'red',
		rejected: 'orange',
		other: 'gray'
	};

	function isABC() {
		return self.filters.programFilter() && self.filters.programFilter().code === 'abc';
	}

	function updateVettingStats(stats) {
		if(stats.statuses.length == 0) {
			self.vettingStats({ state: 'no-data' });
			return;
		}

		let s = { state: 'loaded' };
		let totalCount = 0;
		let statusCounts = {
			unvetted: 0,
			queried: 0
		};
		stats.statuses.forEach(x => {
			totalCount += x.sightings;
			statusCounts[x.code] = (statusCounts[x.code] || 0) + x.sightings;
		});

		let unvettedCount = statusCounts.unvetted + statusCounts.queried;

		if(isABC() && statusCounts.within_range) {
			unvettedCount += statusCounts.within_range;
		}

		s.statuses = stats.statuses.map(x => {
			let percentage =  x.sightings * 100 / totalCount;
			let legendDescription =
				x.name + ": " +
				util.formatNumber(x.sightings) +
				" (" + percentage.toFixed(1) + "%)";
			let colour = statusColours[x.code] || 'gray';
			return {
				percentage,
				legendDescription,
				colour,
				...x
			};
		});
		s.sightingCountDescription = util.formatQuantity(totalCount, "sighting") + " found" + (stats.stale ? "*" : "");
		s.vettedPercentageDescription = (100 * (totalCount - unvettedCount) / totalCount).toFixed(1) + "% vetted" + (isABC() ? "**" : "");
		s.stale = stats.stale;
		s.totalCount = totalCount;
		s.is_abc = isABC();
		self.vettingStats(s);

		let currentVettingStatus = self.filterCriteria().vetting_status;
		self.totalSightingCount(currentVettingStatus ? (statusCounts[currentVettingStatus] || 0) : totalCount);
	}

	let legend;
	self.handleMouseenter = function(model, evt) {
		legend = null;
		let el = evt.target;
		while(el && !legend) {
			legend = el.querySelector(".vetting-legend");
			el = el.parentElement;
		}

		if(legend) {
			updateLegendPosition(evt);
			legend.style.display = 'block';
			legend.style.zIndex = 1000;
			legend.style.pointerEvents = 'none';
		}
	};

	self.handleMousemove = function(model, evt) {
		if(legend) {
			updateLegendPosition(evt);
		}
	};

	self.handleMouseleave = function(model, evt) {
		if(legend) {
			legend.style.display = 'none';
		}
	};

	function updateLegendPosition(evt) {
		legend.style.top = (evt.clientY + 4) + 'px';
		legend.style.right = (window.innerWidth - evt.clientX + 4) + 'px';
	}

	// ------------
	// Bulk review
	// ------------

	self.bulkUpdateInProgress = ko.observable(false);
	self.bulkReviewNotes = ko.observable('');
	self.bulkOtherStatus = ko.observable(null);
	self.vettingStatus.subscribe(self.bulkOtherStatus); // Automatically update bulk status to the current status user is filtering by
	let activeBulkPanel = ko.observable();
	self.showRejectAllPanel = ko.pureComputed(function() {
		return activeBulkPanel() == 'reject';
	});
	self.showAcceptAllPanel = ko.pureComputed(function() {
		return activeBulkPanel() == 'accept';
	});
	self.showOtherAllPanel = ko.pureComputed(function() {
		return activeBulkPanel() == 'other';
	});

	self.toggleRejectAllPanel = function(model, evt) {
		togglePanel('reject', activeBulkPanel, self.bulkReviewNotes, evt);
	}

	self.toggleAcceptAllPanel = function(model, evt) {
		togglePanel('accept', activeBulkPanel, self.bulkReviewNotes, evt);
	}

	self.toggleOtherAllPanel = function(model, evt) {
		togglePanel('other', activeBulkPanel, self.bulkReviewNotes, evt);
	}

	function confirmBulk(status, speciesId) {
		let vettingRequest = {
			notes: self.bulkReviewNotes().trim(),
			vetting_status: status
		};

		if(speciesId) {
			vettingRequest.species_id = speciesId;
		}

		if(self.allSelected()) {
			vettingRequest.criteria = self.filterCriteria();
		} else {
			vettingRequest.sighting_ids = Array.from(selectedSightings);
		}

		self.bulkUpdateInProgress(true);
		return birdata.submitBulkVettingReview(vettingRequest).then(function() {
			refreshVettingStats();
			self.clearSelection();
			return updateSightings(self.filterCriteria(), self.currentCursorData());
		}).catch(function(error) {
			alert("Error while bulk-updating vetting status");
			console.log(error);
		}).finally(function() {
			self.bulkUpdateInProgress(false);
		});
	}

	self.confirmRejectAll = function() {
		confirmBulk('rejected');
	}

	self.confirmAcceptAll = function() {
		confirmBulk('accepted');
	}

	self.confirmOtherAll = function() {
		if(self.bulkOtherStatus()) {
			confirmBulk(self.bulkOtherStatus().code);
		}
	}

	self.closeSurvey = function() {
		self.selectedSurvey(null);
	};

	self.closeHistory = function() {
		self.vettingHistory(null);
	};

	self.editSpeciesAll = function() {
		showEditSpeciesDialog((species) => confirmBulk('accepted', species.id), self.bulkReviewNotes);
	};


	function showEditSpeciesDialog(onConfirm, reviewNotes) {
		let selectedSpecies = ko.observable(null);
		reviewNotes('');

		function close() {
			self.editSpeciesDialog(null);
		}

		function speciesMatchFn(option, text, search) {
			text = text.toLowerCase().replace("-", " ");
			search = search.toLowerCase().replace("-", " ");
			return text.indexOf(search);
		}

		let dialogModel = {
			species: self.species,
			selectedSpecies,
			close,
			speciesMatchFn,
			reviewNotes,
			confirm: function() {
				onConfirm(selectedSpecies()).then(function() {
					close();
				}).catch(function(error) {
					console.log(error);
				});
			},
			confirmEnabled: ko.pureComputed(() => !!selectedSpecies())
		}

		self.editSpeciesDialog(dialogModel)

		return dialogModel;
	}

	self.editSpeciesDialog = ko.observable();

	//---- State handling -------
	var initialState = { ...util.queryParams(), ...util.hashParams() };

	function getState() {
		let criteria = self.filterCriteria();
		if(!criteria) {
			return null;
		}

		var state = { ...criteria };

		delete state.xmin;
		delete state.xmax;
		delete state.ymin;
		delete state.ymax;

		delete state.supertaxa;

		delete state.permit_stale;

		if(self.filters.visibleOnMapFilter()) {
			state.visible_on_map = 'true';
		}

		if(self.map()) {
			var map = self.map();
			self.mapBounds(); // trigger ko binding
			var center = map.getCenter();

			state.map = [center.lat().toFixed(7), center.lng().toFixed(7), map.getZoom()].join('_');
		}

		if(!state.vetting_status) {
			state.vetting_status = 'all';
		}

		state = Object.fromEntries(
			Object.entries(state).filter(([k, v]) => !!v));

		return state;
	}

	ko.computed(function() {
		let state = getState();
		if(state) {
			history.replaceState({}, '', '#' + util.encodeParams(state));
		}
	});
	//---- End state handling ---

	self.loadingTiles = ko.observable(0);
	self.visibleTiles = ko.observable(0);
	self.tilesInBatch = highWaterMark(self.loadingTiles);

	function highWaterMark(obs) {
		var prev = obs();
		var mark = obs();
		return ko.pureComputed(function() {
			var n = obs();
			if(n > prev) {
				mark = n;
			}
			prev = n;
			return mark;
		});
	}

	self.tileLoadingProgress = ko.pureComputed(function() {
		const x = self.loadingTiles();
		const n = self.tilesInBatch();

		// Note, if loading off-screen tiles due to fast map changes, progress could be
		// negative, so we set a minimum of 0.
		const percent = Math.max(0, Math.round(n == 0 ? 100 : (n - x) * 100 / n));

		return "Loading " + percent + "%";
	}).extend({ rateLimit: 50 });

	self.tilesLoaded = ko.pureComputed(function() {
		return self.loadingTiles() == 0;
	});

	function setupMap(map) {
		if(!map || setupMap.called) return;
		setupMap.called = true;

		let individualMarkerZoomThreshold = 13;

		self.sightingMarker = new google.maps.Marker({ map });

		const hoverOverlay = googleMaps.hoverOverlay(map);

		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.dragZoomControl(map));

		const overlayCriteria = ko.pureComputed(function() {
			return { ...self.filterCriteria() };
		});

		const highlightRecent = true;

		const overlay = new DensityMapType({
			tileFn: birdata.vettableDensityTile,
			maxZoom: individualMarkerZoomThreshold - 1,
			tileSize: new google.maps.Size(256, 256),
			map: map,
			criteriaFn: overlayCriteria,
			visibleTiles: self.visibleTiles,
			loadingTiles: self.loadingTiles,
			highlightRecent: false,
			events: {
				mousemove: function(evt, info) {
					if(info.latLng) {
						hoverOverlay.position(info.latLng);
						// evt.stopPropagation();
					}
				},
				mouseover: function(evt, info) {
					if(info.latLng) {
						var count = evt.target.getAttribute('data-count');
						var recent = evt.target.getAttribute('data-recent') === "true";
						if(count) {
							hoverOverlay.position(info.latLng);

							var noun = "sighting"
							var html = '<div>' + util.formatQuantity(+count, noun) + '</div>'

							hoverOverlay.html(html);
							hoverOverlay.show();
							// evt.stopPropagation();
						}
					}
				},
				mouseout: function(evt, info) {
					hoverOverlay.hide();
					// evt.stopPropagation();
				}
			},
			highlightRecent: highlightRecent
		});

		overlayCriteria.subscribe(function(){
			// Small delay to ensure other requests happen first.
			// Otherwise map tile loading can delay loading of more important UI elements
			setTimeout(function() {
				overlay.refresh();
			}, 10);
		});
		map.overlayMapTypes.insertAt(0, overlay);

		var polygonEvents = {
			mousemove: function(evt) {
				hoverOverlay.position(evt.latLng);
			},
			mouseover: function(evt) {
				var featureID = hoverOverlay.featureID = this.userData.id;
				var layerID = hoverOverlay.layerID = this.userData.layerID;
				hoverOverlay.position(evt.latLng);

				hoverContentPromise(this).then(function(content) {
					if(hoverOverlay.featureID != featureID || hoverOverlay.layerID != layerID) return; // ignore stale

					hoverOverlay.html(content);
					hoverOverlay.show();
				});
			},
			mouseout: function(evt) {
				hoverOverlay.featureID = hoverOverlay.layerID = null;
				hoverOverlay.hide();
			},
		};

		function hoverContentPromise(poly) {
			switch(poly.userData.layerID) {
				case "areas":
					return Promise.all([
						birdata.area(poly.userData.id),
						areasMap.significanceDescription(poly.userData.id)
					]).then(([area, description]) => {
						if(area) {
							return area.name + (description ? "<hr>" + description : "");
						} else {
							return null;
						}
					});
			}
		}


		const areaMapLayer = areasMap.showLayer(map, {
			layerID: "areas",
			polygonEvents: polygonEvents
		});

		ko.computed(function() {
			var areaLayer = self.filters.areaLayerFilter();
			var area = self.filters.areaFilter();
			areaMapLayer.setFeatureFilter(function(feature) {
				var attrs = feature.userData && feature.userData.attributes;
				if(!attrs) return true; //Needed?
				if(!areaLayer) return false;
				if(!area) return areaLayer.id == attrs.layer_id;
				else return area.id == attrs.id;
			});
		});
		areaMapLayer.refresh();

		const regionMapLayer = regionsMap.showLayer(map, {
			layerID: "regions"
		});

		ko.computed(function() {
			var region = self.filters.regionFilter();
			regionMapLayer.setFeatureFilter(function(feature) {
				var attrs = feature.userData && feature.userData.attributes;
				if(!attrs) return true; //Needed?
				if(!region) return false;
				return region.id == attrs.id;
			});
		});
		regionMapLayer.refresh();

		let markerIcons = {};
		Object.entries(statusColours).forEach(([name, color]) => {
			markerIcons[name] = mapStyles.preRenderedIcon({
				path: google.maps.SymbolPath.CIRCLE,
				fillColor: color,
				fillOpacity: 1,
				strokeColor: "#FFFFFF",
				strokeWeight: 2,
				scale: 6
			}, "circle");
		});

		const surveyPointLayer = markerLayer(map, {
			minZoom: individualMarkerZoomThreshold,
			visibleTilesObs: self.visibleTiles,
			loadingTilesObs: self.loadingTiles,
			progressiveLoading: true,
			pointsForTile: function(tile) {
				let params = {
					tile: [tile.x, tile.y, tile.z].join(','),
					...self.filterCriteria()
				};
				delete params.vetting_status;
				return birdata.sightingsWithLocations(params).then(function(points) {
					if(tile.removed) return;
					points.forEach(function(p) {
						// Populate ID needed by marker layer
						p.id = p.lat + "," + p.lon;
					});
					return points;
				});
			},
			markerForPoint: function(p) {
				let title = util.formatQuantity(p.count, "sighting");

				p.vetting_status = p.vetting_status_codes.includes("queried") ? "queried" : p.vetting_status_codes[0];

				var marker = new google.maps.Marker({
					position: new google.maps.LatLng(p.lat, p.lon),
					title: title,
					icon: markerIcons[p.vetting_status] || markerIcons['other'],
					zIndex: (p.vetting_status == 'queried') ? (google.maps.Marker.MAX_ZINDEX + p.lat + 1000) : (google.maps.Marker.MAX_ZINDEX + p.lat)
				});

				google.maps.event.addListener(marker, 'click', function() {
					showInfoWindow(p, marker, {disableAutoPan: self.filters.visibleOnMapFilter() });
				});

				if(p.id == infoWindowMarkerId) {
					showInfoWindow(p, marker, { disableAutoPan: true });
				}

				return marker;
			}
		});
		// TODO: Work out why this is necessary and fix it
		surveyPointLayer.refresh();

		var infoWindow;

		// Track which marker has an info window open so that we can re-show it if markers are reloaded
		let infoWindowMarkerId = null;

		// Dismiss info window by clicking anywhere outside
		google.maps.event.addListener(map, 'click', function() {
			if(infoWindow) {
				infoWindow.close();
				infoWindowMarkerId = null;
			}
		});

		function showInfoWindow(p, marker, infoWindowOptions = {}) {
			if(infoWindow) {
				infoWindow.close();
			}

			infoWindow = new google.maps.InfoWindow({
				content: infoWindowContent(p),
				...infoWindowOptions
			});
			infoWindow.open(map, marker);

			infoWindowMarkerId = p.id;
			google.maps.event.addListener(infoWindow, 'closeclick', function() {
				infoWindowMarkerId = null;
			});
		}

		function infoWindowContent(p) {
			let criteria = {
				lat: p.lat,
				lon: p.lon,
				...self.filterCriteria()
			};
			delete criteria.vetting_status;
			return createPagedSightingList(criteria);
		}

		self.filterCriteria.subscribe(function() {
			// Add a small delay so that other requests are initiated first.
			// Otherwise the map tile requests can block other more visually important requests
			setTimeout(function() {
				surveyPointLayer.refresh();
			}, 50);
		});

		google.maps.event.addListener(map, 'idle', function() {
			self.mapBounds(map.getBounds());
		});
		self.mapBounds(map.getBounds());

		// Auto zoom to area/region
		let autoZoomFirstCall = true;
		ko.computed(function() {
			var area = self.filters.areaFilter();
			var areaLayer = self.filters.areaLayerFilter();
			var region = self.filters.regionFilter();

			if(autoZoomFirstCall) {
				autoZoomFirstCall = false;
				return;
			}

			if(initialState.map && !self.filters.haveAppliedInitialState) return;

			var bounds;
			if(area && area.bounds) bounds = googleMaps.bounds(area.bounds, bounds);
			// Only zoom to entire area layer for SOP
			if(!area && areaLayer && areaLayer.code == 'sop') bounds = googleMaps.bounds(areaLayer.bounds, bounds);
			if(region) bounds = googleMaps.bounds(region.bounds, bounds);
			if(bounds) map.fitBounds(bounds);
		});
	}

	function isVettingPermission(permission) {
		return permission.role === 'admin' || permission.role == 'vetter';
	}

	let lastSelectedPermission = null;
	function applyFiltersForPermission(permission) {
		if(permission == null || self.filters.lockedFilters == null) {
			return;
		}
		if(lastSelectedPermission) {
			self.filters.programFilter(null);
			self.filters.areaFilter(null);
			self.filters.areaLayerFilter(null);
			self.filters.multipleSpeciesFilter([]);
			self.filters.regionFilter(null);
		}
		lastSelectedPermission = permission;

		if(permission.type === 'program') {
			self.filters.selectProgram(permission.program);
			if(permission.area_id) {
				self.filters.selectArea(permission.area_id);
				self.filters.lockedFilters(['program', 'area']);
			} else {
				self.filters.lockedFilters(['program']);
			}
		} else if(permission.type === 'species') {
			self.filters.selectSpecies(permission.species_id);
			self.filters.lockedFilters(['species']);
		} else if(permission.type == 'region') {
			self.filters.selectRegion(permission.region_id);
			self.filters.lockedFilters(['region']);
		} else {
			self.filters.lockedFilters([]);
		}
	}

	self.roleOptions = ko.observable([]);
	self.activeRole = ko.observable(null);

	self.activeRole.subscribe(applyFiltersForPermission);

	let mePromise = birdata.me();
	load(mePromise.then(function(me) {

		let roles = [];
		if(me.is_administrator) {
			roles.push({ description: 'Global Administrator' });
		}
		for(let p of me.permissions) {
			if(isVettingPermission(p)) {
				roles.push({
					description: p.role_description + " (" + p.resource_description + ")",
					...p
				});
			}
		}
		self.roleOptions(roles);

		self.filters.initFn = function() {
			self.activeRole(roles[0]);
		};
	}));

	Promise.all(loadPromises).then(() => {
		self.loaded(true);
	});
}

ko.components.register('vetting', {
	viewModel: function() {
		return new VettingViewModel(birdata);
	},
	template: require('../../templates/vetting.html')
});

export default VettingViewModel;
